import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Box, Tabs, Title } from "@mantine/core";
import { isAdmin, useSupervisorAccess } from "../../shared/AuthContext";
import { Loading, NotFoundPage, usePageTitle } from "../../shared/utils";
import { SemestersTab } from "./tabs/SemestersTab";
import { SubjectsTab } from "./tabs/SubjectsTab";
import { RegistrationsTab } from "./tabs/RegistrationsTab";
import { UsersTab } from "./tabs/UsersTab";
import { AwhTab } from "./tabs/AwhTab";
import { AwhExportsTab } from "./tabs/AwhExport";

// `supervisorOnly` tabs are hidden (and their routes blocked) unless the user has
// the supervisor role — even for other admins.
// Channels/playlists moved to its own /playlists page (visible to everyone,
// not just admins) — see features/playlists/Playlists.tsx.
const TABS: { value: string; label: string; Component: () => JSX.Element; supervisorOnly?: boolean }[] = [
  { value: "semesters", label: "Семестры", Component: SemestersTab, supervisorOnly: true },
  { value: "subjects", label: "Предметы", Component: SubjectsTab },
  { value: "registrations", label: "Регистрации", Component: RegistrationsTab },
  { value: "users", label: "Пользователи", Component: UsersTab },
  { value: "awh", label: "ЧРА", Component: AwhTab, supervisorOnly: true },
  { value: "awh-exports", label: "Выгрузки ЧРА", Component: AwhExportsTab, supervisorOnly: true },
];

export function Admin() {
  usePageTitle("Админка");
  const { tab } = useParams();
  const navigate = useNavigate();
  const { value: isAdminValue } = isAdmin();
  const { value: isSupervisor } = useSupervisorAccess();

  if (isAdminValue === undefined || isSupervisor === undefined) {
    return <Loading />;
  }
  if (isAdminValue === false) {
    return <NotFoundPage />;
  }

  const visibleTabs = TABS.filter(t => !t.supervisorOnly || isSupervisor);
  const active = visibleTabs.find(t => t.value === tab);
  if (!active) {
    return <Navigate to={`/admin/${visibleTabs[0]?.value ?? "subjects"}`} replace />;
  }
  const ActiveComponent = active.Component;

  return (
    <Box component="main" p="xl">
      <Box maw={1100} mx="auto" px="xs">
        <Title order={2} mb="lg">Админ-панель</Title>
        <Tabs value={tab} onChange={value => value && navigate(`/admin/${value}`)} mb="xl">
          <Tabs.List>
            {visibleTabs.map(t => (
              <Tabs.Tab key={t.value} value={t.value}>{t.label}</Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <ActiveComponent />
      </Box>
    </Box>
  );
}
